import React, { ChangeEvent, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import styled, { createGlobalStyle } from 'styled-components';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { Libraries } from '@react-google-maps/api/dist/utils/make-load-script-url';

import { StyledFormControl } from '../styled/Inputs';
import { AddressFieldType, AddressLabels, AddressObject, IUAddressLabel } from './AddressFields';
import { DeleteIcon, DropDownClickable, FieldCol } from '../styled/ContactsForm';
import trash from '../../core/assets/trash.png';

import { TAppContact } from 'TProtocol/common/models';
import GeocoderAddressComponent = google.maps.GeocoderAddressComponent;
import GAutocomplete = google.maps.places.Autocomplete;

declare const AUTOCOMPLETE_KEY: string;

interface Props {
  contact: TAppContact;
  handleDelete: () => void;
  handleChange: (value: string, fieldAttribute: AddressFieldType) => void;
  handleLabelChange: (address: AddressObject, previousLabel: IUAddressLabel) => void;
  address: AddressObject;
  autocomplete?: boolean;
}

const LIBRARIES: Libraries = ['places'];

function GeocompleteInternal({ address, handleDelete, handleLabelChange, handleChange, autocomplete = false }: Props) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: AUTOCOMPLETE_KEY,
    libraries: LIBRARIES,
  });

  const [gautocomplete, setGautocomplete] = useState<GAutocomplete | null>(null);
  const [address1, setAddress1] = useState<string>('');
  const [address2, setAddress2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [label, setLabel] = useState<IUAddressLabel>(address.label ?? IUAddressLabel.OtherAddress);

  useEffect(() => {
    setLabel(address.label);
  }, [address.label]);

  const onLoad = (autocomplete: GAutocomplete) => {
    autocomplete.setFields(['address_components', 'name']);
    autocomplete.setTypes(['address']);
    setGautocomplete(autocomplete);
  };

  useEffect(() => {
    onPlaceChanged();
  }, []);

  const onPlaceChanged = () => {
    if (gautocomplete === null) {
      return;
    }
    const addressObject = gautocomplete.getPlace();
    const address = addressObject.address_components;

    if (address) {
      let name = (!addressObject.name?.match(/^\d/) && addressObject.name + ', ') || '';

      let subPremiseObj;
      if (address[0].types.includes('subpremise')) {
        subPremiseObj = address.shift();
      }
      const subPremiseVal = subPremiseObj?.long_name;

      if (address[0].long_name !== addressObject.name) {
        name = name + address[0].long_name;
      }
      const address1Val = `${name} ${address[1].long_name}` ?? '';

      const cityObj = address.filter((component: GeocoderAddressComponent) =>
        component.types.includes('locality')
      )[0];
      const cityVal: string = (cityObj && cityObj.long_name) ?? '';

      const admin1Obj = address.filter((component: GeocoderAddressComponent) =>
        component.types.includes('administrative_area_level_1')
      )[0];
      const admin1Val: string = (admin1Obj && admin1Obj.short_name) ?? '';

      const zipObj = address.filter((component: GeocoderAddressComponent) =>
        component.types.includes('postal_code')
      )[0];
      const zipVal: string = (zipObj && zipObj.short_name) ?? '';

      if (subPremiseVal !== undefined) {
        setAddress2(subPremiseVal);
      }
      setAddress1(address1Val);
      setCity(cityVal);
      setPostalCode(zipVal);

      handleAddressChangeFromAutoFill(address1Val, 'address1');
      if (subPremiseVal !== undefined) {
        handleAddressChangeFromAutoFill(subPremiseVal, 'address2');
      }
      handleAddressChangeFromAutoFill(cityVal, 'city');
      handleAddressChangeFromAutoFill(admin1Val, 'admin1');
      handleAddressChangeFromAutoFill(zipVal, 'postalCode');
    }
  };

  useEffect(() => {
    if (address.address?.address1) {
      setAddress1(address.address.address1);
    }
    if (address.address?.address2) {
      setAddress2(address.address.address2);
    }
    if (address.address?.city) {
      setCity(address.address.city);
    }
    if (address.address?.postalCode) {
      setPostalCode(address.address.postalCode);
    }
  }, []);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldAttribute: AddressFieldType) => {
    const value = e.target.value;
    if (fieldAttribute === 'address1') {
      setAddress1(value);
    } else if (fieldAttribute === 'address2') {
      setAddress2(value);
    } else if (fieldAttribute === 'city') {
      setCity(value);
    } else if (fieldAttribute === 'postalCode') {
      setPostalCode(value);
    } else {
      return;
    }
    handleChange(value, fieldAttribute);
  };

  const handleAddressChangeFromAutoFill = (value: string, fieldAttribute: string) => {
    if (fieldAttribute === 'address1' || fieldAttribute === 'address2' || fieldAttribute === 'city' ||
      fieldAttribute === 'postalCode' || fieldAttribute === 'admin1') {
      handleChange(value, fieldAttribute);
    }
  };

  if (!isLoaded) {
    return null;
  }

  const onLabelChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newLabel = +e.target.value as IUAddressLabel;

    setLabel(newLabel);

    handleLabelChange({
      address: address.address,
      label: newLabel
    }, label);
  };

  const options = Object.keys(IUAddressLabel)
    .filter((key) => {
      const labelOption = Number(key) as IUAddressLabel;

      if (isNaN(labelOption)) {
        return false;
      }

      return true;
    })
    .map((labelStr) => {
      const label = +labelStr as IUAddressLabel;
      return <option value={label} key={label}>{AddressLabels[label]}</option>;
    });

  return (
    <Autocomplete
      onLoad={onLoad}
      onPlaceChanged={onPlaceChanged}
    >
      <div>
        <Form.Group as={Row}>
          <FieldCol>
            <StyledFormControl
              autoComplete={autocomplete && label === IUAddressLabel.PersonalAddress ? 'address-line1' : 'do-not-autocomplete'}
              className="google-auto-complete-input"
              value={address1}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleAddressChange(e, 'address1')}
              placeholder="Street Address 1"
            />
          </FieldCol>
        </Form.Group>
        <Form.Group as={Row}>
          <FieldCol>
            <StyledFormControl
              autoComplete={autocomplete && label === IUAddressLabel.PersonalAddress ? 'address-line2' : 'do-not-autocomplete'}
              value={address2}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleAddressChange(e, 'address2')}
              placeholder="Street Address 2"
            />
          </FieldCol>
        </Form.Group>
        <Form.Group as={Row} className="gx-2">
          <FieldCol lg={4} xs={12}>
            <StyledFormControl
              autoComplete={autocomplete && label === IUAddressLabel.PersonalAddress ? 'address-level2' : 'do-not-autocomplete'}
              value={city}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleAddressChange(e, 'city')}
              placeholder="City"
            />
          </FieldCol>
          <FieldCol lg={3} xs={6}>
            <StyledFormControl
              autoComplete={autocomplete && label === IUAddressLabel.PersonalAddress ? 'address-level1' : 'do-not-autocomplete'}
              value={postalCode}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleAddressChange(e, 'postalCode')}
              placeholder="Zip"
            />
          </FieldCol>
          <FieldCol lg={4} xs={5}>
            <DropDownClickable
              value={label}
              onChange={onLabelChange}
            >
              {options}
            </DropDownClickable>
          </FieldCol>
          <FieldCol xs={1}>
            <DeleteIcon src={trash} onClick={handleDelete}/>
          </FieldCol>
        </Form.Group>
      </div>
    </Autocomplete>
  );
}

createGlobalStyle`
  .pac-container .pac-item {
    cursor: pointer !important;
  }

  .pac-container.pac-logo.hdpi::after {
    display: none !important;
  }
`;
const Geocomplete = styled(GeocompleteInternal)`

`;
export default Geocomplete;
